"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Swiper as SwiperType } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"
import {
  extractProperty,
  FacilityContent,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
  PortItem,
} from "utils"
import PortContainer from "./PortContainer"
import PortPrimaryButton from "./PortPrimaryButton"
import PortTitle from "./PortTitle"
import { Location3Icon, PriceIcon, UserIcon } from "./icons"

const StyledSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  padding: 60px 0;
  gap: 24px;
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 90px 0;
    gap: 40px;
  }
`

const CARD_WIDTH = 325
const DESKTOP_GAP = 32

const StyledSwiperWrapper = styled.div`
  width: 100%;
  .swiper {
    padding: 0 20px;
    overflow: visible;
  }
  .swiper-wrapper {
    transition-timing-function: ease-in-out;
  }
  .swiper-slide {
    width: ${CARD_WIDTH}px;
    height: auto;
  }
`

const StyledFlexCards = styled.div`
  display: flex;
  justify-content: center;
  gap: ${DESKTOP_GAP}px;
  padding: 0 20px;
`

const StyledFlexCard = styled.div`
  width: ${CARD_WIDTH}px;
  flex-shrink: 0;
`

const StyledPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  margin-bottom: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: none;
  }
`

const StyledPaginationBullet = styled.button<{ $isActive: boolean }>`
  width: 17px;
  height: 17px;
  padding: 0;
  background-color: transparent;
  border: ${({ $isActive, theme }) =>
    $isActive ? `0.5px solid ${theme.colors.propBlue}` : "none"};
  border-radius: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::after {
    content: "";
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background-color: ${({ $isActive, theme }) =>
      $isActive ? theme.colors.propBlue : theme.colors.port.grey};
    transition: background-color 0.3s ease;
  }

  &:hover {
    border-color: ${(props) => props.theme.colors.propBlue};
  }
`

const StyledCard = styled(Link)`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: ${(props) => props.theme.colors.white};
  border: 1px solid ${(props) => props.theme.colors.port.grey};
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.3s ease;
  &:hover {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  }
`

const StyledCardImage = styled.div`
  width: 100%;
  height: 259px;
  position: relative;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 16px 24px;
  gap: 16px;
  flex: 1;
  width: 100%;
`

const StyledCardTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 300;
  line-height: 150%;
`

const StyledDetailList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const StyledDetailItem = styled.li`
  list-style: none;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 300;
  line-height: 150%;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`

const StyledCardButton = styled(Button)`
  margin-top: auto;
  width: 100%;
  border: 1px solid ${(props) => props.theme.colors.port.defaultTextColor};
  border-radius: 45px;
  color: ${(props) => props.theme.colors.port.defaultTextColor};
  font-size: 18px;
  font-weight: 700;
  line-height: 150%;
  padding: 12px 16px;
  height: 40px;
  text-transform: none;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: ${(props) => props.theme.colors.port.defaultTextColor};
    color: ${(props) => props.theme.colors.white};
  }
`

type PortMeetingRoomsProps = {
  data: any
  ports?: PortItem[]
}

const RoomCard = ({ room }: { room: FacilityContent }) => (
  <StyledCard
    href={room.button?.url ? getNonUmbracoUrl(room.button.url) : getNonUmbracoUrl(room.url)}
  >
    <StyledCardImage>
      {room.image && (
        <Image
          src={getUmbracoMediaUrl(room.image)}
          width={300}
          height={200}
          alt={room.facilityName || "Meeting Room"}
        />
      )}
    </StyledCardImage>
    <StyledCardContent>
      <StyledCardTitle>{room.facilityName}</StyledCardTitle>
      <StyledDetailList>
        {room.address && (
          <StyledDetailItem>
            <Location3Icon />
            <span>{room.address}</span>
          </StyledDetailItem>
        )}
        {room.amountOfPeople && (
          <StyledDetailItem>
            <UserIcon />
            <span>{room.amountOfPeople}</span>
          </StyledDetailItem>
        )}
        {room.price && (
          <StyledDetailItem>
            <PriceIcon />
            <span>{room.price}</span>
          </StyledDetailItem>
        )}
      </StyledDetailList>
      <StyledCardButton variant="outlined" tabIndex={-1} aria-hidden="true">
        {room.button?.name || "להזמנה אונליין"}
      </StyledCardButton>
    </StyledCardContent>
  </StyledCard>
)

const PortMeetingRooms = ({ data, ports }: PortMeetingRoomsProps) => {
  const blackTitle = extractProperty(data, "blackTitle")
  const blueTitle = extractProperty(data, "blueTitle")
  const subtitle = extractProperty(data, "subtitle")
  const viewAllButton = extractProperty(data, "viewAllButton")?.[0]
  const wrapperRef = useRef<HTMLDivElement>(null)
  const swiperRef = useRef<SwiperType | null>(null)
  const [needsSwiper, setNeedsSwiper] = useState(true)
  const [activeIndex, setActiveIndex] = useState(2)

  const handleBulletClick = useCallback((index: number) => {
    swiperRef.current?.slideTo(index)
  }, [])

  const meetingRooms: FacilityContent[] = useMemo(() => {
    const allFacilities = ports?.flatMap((port) => port.portFacilities || []) || []
    return allFacilities.filter(
      (f) =>
        f.facilityType?.toLowerCase().includes("חדר") ||
        f.facilityType?.toLowerCase().includes("meeting"),
    )
  }, [ports])

  // Always use swiper — design shows carousel with centered 3rd card on both desktop and mobile
  useEffect(() => {
    setNeedsSwiper(true)
  }, [])

  if (!blackTitle && !blueTitle && meetingRooms.length === 0) {
    return null
  }

  return (
    <StyledSection ref={wrapperRef}>
      <PortContainer paddingInline={20}>
        <PortTitle
          normalText={blackTitle}
          centered
          highlightedText={blueTitle}
          subtitle={subtitle || ""}
          subtitleSx={{
            fontWeight: { xs: "300" },
          }}
        />
      </PortContainer>
      {needsSwiper ? (
        <StyledSwiperWrapper>
          <Swiper
            slidesPerView="auto"
            spaceBetween={16}
            centeredSlides
            initialSlide={meetingRooms.length > 2 ? 2 : 0}
            onSwiper={(swiper) => {
              swiperRef.current = swiper
            }}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
            breakpoints={{
              768: {
                spaceBetween: 32,
              },
            }}
          >
            {meetingRooms.map((room) => (
              <SwiperSlide key={room.id}>
                <RoomCard room={room} />
              </SwiperSlide>
            ))}
          </Swiper>
          {meetingRooms.length > 1 && (
            <StyledPagination>
              {meetingRooms.map((_, index) => (
                <StyledPaginationBullet
                  key={`bullet-${index}`}
                  $isActive={index === activeIndex}
                  onClick={() => handleBulletClick(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </StyledPagination>
          )}
        </StyledSwiperWrapper>
      ) : (
        <StyledFlexCards>
          {meetingRooms.map((room) => (
            <StyledFlexCard key={room.id}>
              <RoomCard room={room} />
            </StyledFlexCard>
          ))}
        </StyledFlexCards>
      )}
      <PortContainer paddingInline={20}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <PortPrimaryButton
            button={viewAllButton}
            sx={{
              height: "46px",
            }}
          />
        </Box>
      </PortContainer>
    </StyledSection>
  )
}
export default PortMeetingRooms
